import { BigNumber } from 'bignumber.js';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import type { UnclaimedRecipeProps } from '~/types/crafting';
import { WebSocketsKeys } from '~/types/common';

export interface IUnclaimedModifierData {
  userRecipeIndex: number;
  unclaimedModifier: number;
  multiplyModifier: number;
  buildingHash: string;
  recipeId: string;
  recipe: UnclaimedRecipeProps;
}

export default function useUnclaimedModifiers() {
  if (!import.meta.client) {
    return {
      fetchUnclaimedModifiersFromAPI: async () => {},
      fetchRecipeData: async () => {}
    };
  }

  const { address } = useWeb3ModalAccount();
  const { getUserActivitiesShort } = useUserActivityManager();
  const store = useMainStore();

  // Local Maps and controllers to manage fetches
  const scheduledFetches = new Map<number, NodeJS.Timeout[]>();
  const ongoingRecipeFetches = new Map<number, AbortController>();
  let currentAbortController: AbortController | null = null;

  const nuxtApp = useNuxtApp();
  const websockets = nuxtApp.$websockets;
  const craftingSocket = websockets[WebSocketsKeys.craftingActivity];

  const craftingSocketData = computed(() => craftingSocket.parsedData);
  const uniqueRecipes = computed(() =>
    craftingSocketData.value.filter(
      (recipe: UnclaimedRecipeProps, index: number, self: UnclaimedRecipeProps[]) =>
        self.findIndex((t) => t.recipeId === recipe.recipeId) === index
    )
  );

  // All states are now managed by the store

  // getUnclaimedModifier is now handled by the store

  const fetchUnclaimedModifiersFromAPI = async (signal?: AbortSignal): Promise<void> => {
    if (!address.value || !uniqueRecipes.value) return;

    try {
      store.setIsLoadingUnclaimedModifiers(true);
      const claimItemsData = await getUserActivitiesShort(uniqueRecipes.value, signal);

      if (signal?.aborted) {
        return;
      }
      store.setCachedClaimItems(claimItemsData);

      const modifiersMap: Record<number, IUnclaimedModifierData> = {};

      claimItemsData.forEach((item) => {
        Object.values(item.unclaimedByRecipeIndexes).forEach((modifierData) => {
          modifiersMap[modifierData.userRecipeIndex] = modifierData;
        });
      });
      store.setUnclaimedModifiers(modifiersMap);
    } catch (error) {
      if (error instanceof Error && error.message !== 'Request was cancelled') {
        console.error('Error fetching unclaimed modifiers from API:', error);
      }
    } finally {
      store.setIsLoadingUnclaimedModifiers(false);
    }
  };

  // Fetch data for a specific recipe
  const fetchRecipeData = async (recipe: UnclaimedRecipeProps): Promise<void> => {
    const recipeKey = recipe.userRecipeIndex;

    const existingController = ongoingRecipeFetches.get(recipeKey);
    if (existingController) {
      existingController.abort();
    }

    const controller = new AbortController();
    ongoingRecipeFetches.set(recipeKey, controller);

    const abortSignal = controller.signal;

    try {
      const updatedClaimItems = await getUserActivitiesShort([recipe], abortSignal);

      if (abortSignal.aborted) {
        return;
      }

      if (updatedClaimItems.length > 0) {
        const updatedItem = updatedClaimItems[0];

        const currentClaimItems = [...store.cachedClaimItems];
        const existingItemIndex = currentClaimItems.findIndex((item) =>
          BigNumber(item.recipe.recipeId).eq(recipe.recipeId)
        );

        if (existingItemIndex !== -1) {
          currentClaimItems[existingItemIndex] = updatedItem;
        } else {
          currentClaimItems.push(updatedItem);
        }

        store.setCachedClaimItems(currentClaimItems);

        const currentModifiers = { ...store.unclaimedModifiers };
        Object.values(updatedItem.unclaimedByRecipeIndexes).forEach((modifierData) => {
          currentModifiers[modifierData.userRecipeIndex] = modifierData;
        });
        store.setUnclaimedModifiers(currentModifiers);
      }
    } catch (error) {
      if (error instanceof Error && error.message !== 'Request was cancelled') {
        console.error(`Error fetching recipe data for ${recipe.recipeId}:`, error);
      }
    } finally {
      ongoingRecipeFetches.delete(recipeKey);
    }
  };

  const scheduleFetchForCompletion = (recipe: UnclaimedRecipeProps): void => {
    // Clear existing scheduled fetches for this recipe
    const existingTimeouts = scheduledFetches.get(recipe.userRecipeIndex);
    if (existingTimeouts) {
      existingTimeouts.forEach((timeout) => clearTimeout(timeout));
    }

    const DELAY_SAFETY_OFFSET = 1000;
    const startTime = new Date(recipe.craftStartTime).getTime();
    const singleCraftDuration = recipe.craftDuration * 1000;
    const now = Date.now();

    const timeouts: NodeJS.Timeout[] = [];

    // Schedule a fetch for each completion based on multiplyModifier
    for (let i = 1; i <= recipe.multiplyModifier; i++) {
      const completionTime = startTime + singleCraftDuration * i;
      const delay = Math.max(0, completionTime - now + DELAY_SAFETY_OFFSET);

      const timeout = setTimeout(async () => {
        if (address.value) {
          await fetchRecipeData(recipe);
        }

        // Remove this specific timeout from the array
        const currentTimeouts = scheduledFetches.get(recipe.userRecipeIndex);
        if (currentTimeouts) {
          const index = currentTimeouts.indexOf(timeout);
          if (index > -1) {
            currentTimeouts.splice(index, 1);
          }

          // If no more timeouts for this recipe, remove the entry
          if (currentTimeouts.length === 0) {
            scheduledFetches.delete(recipe.userRecipeIndex);
          }
        }
      }, delay);

      timeouts.push(timeout);
    }

    scheduledFetches.set(recipe.userRecipeIndex, timeouts);
  };

  watch(
    () => craftingSocket.parsedData,
    async (newData: UnclaimedRecipeProps[]) => {
      if (currentAbortController) {
        currentAbortController.abort();
      }

      if (newData && newData.length > 0 && address.value) {
        const controller = new AbortController();
        currentAbortController = controller;
        const signal = controller.signal;

        try {
          await fetchUnclaimedModifiersFromAPI(signal);

          if (!signal.aborted) {
            newData.forEach((recipe) => {
              scheduleFetchForCompletion(recipe);
            });
          }
        } catch (error) {
          if (error instanceof Error && error.message !== 'Request was cancelled') {
            console.error('Error in watch handler:', error);
          }
        } finally {
          currentAbortController = null;
        }
      } else {
        store.setUnclaimedModifiers({});
        store.clearCachedClaimItems();
        scheduledFetches.forEach((timeouts) => timeouts.forEach((timeout) => clearTimeout(timeout)));
        scheduledFetches.clear();
      }
    },
    { immediate: true }
  );

  onUnmounted(() => {
    if (currentAbortController) {
      currentAbortController.abort();
    }

    ongoingRecipeFetches.forEach((controller) => controller.abort());
    ongoingRecipeFetches.clear();

    scheduledFetches.forEach((timeouts) => timeouts.forEach((timeout) => clearTimeout(timeout)));
    scheduledFetches.clear();
  });

  return {
    fetchUnclaimedModifiersFromAPI,
    fetchRecipeData
  };
}
